import React, { useState, useEffect, useMemo } from 'react';
import { rolesAPI, menusAPI } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Plus, Edit, Trash2, Shield, Search, X, Eye } from 'lucide-react';
import { toast } from 'sonner';
import CreateRolDialog from '../components/CreateRolDialog';
import EditRolDialog from '../components/EditRolDialog';
import { usePermissions } from '../hooks/usePermissions';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedRol, setSelectedRol] = useState(null);
  const [viewReadOnly, setViewReadOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { isAdmin, canCreate, canView, canEdit, canDelete, ready } = usePermissions();

  useEffect(() => {
    if (!ready) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const loadData = async () => {
    if (!canView('/roles') && !isAdmin()) {
      toast.error('No tiene permisos para ver roles');
      setLoading(false);
      return;
    }

    try {
      const [rolesRes, menusRes] = await Promise.all([
        rolesAPI.getAll(),
        menusAPI.getAll(),
      ]);
      setRoles(rolesRes.data);
      setMenus(menusRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rol) => {
    if (!canEdit('/roles') && !isAdmin()) {
      toast.error('No tiene permisos para editar roles');
      return;
    }
    setSelectedRol(rol);
    setViewReadOnly(false);
    setShowEdit(true);
  };

  const handleView = (rol) => {
    setSelectedRol(rol);
    setViewReadOnly(true);
    setShowEdit(true);
  };

  const handleDelete = async (rol) => {
    if (!canDelete('/roles') && !isAdmin()) {
      toast.error('No tiene permisos para eliminar roles');
      return;
    }

    if (!window.confirm(`¿Está seguro de eliminar el rol "${rol.nombre}"?`)) return;

    try {
      await rolesAPI.delete(rol.id);
      toast.success('Rol eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar rol');
    }
  };

  const getPermissionSummary = (permisos) => {
    if (!permisos || permisos.length === 0) return 'Sin permisos';
    const totalMenus = menus.length;
    const menusWithPermission = permisos.length;
    return `${menusWithPermission} de ${totalMenus} menús`;
  };

  // Mapa {menu_id: menu_nombre} para resolver permisos → nombre legible
  const menusMap = useMemo(
    () => Object.fromEntries(menus.map((m) => [m.id, m.nombre])),
    [menus]
  );

  // Filtrado + orden alfabético (izq→der, arriba→abajo por flujo natural del CSS grid)
  const filteredAndSorted = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const norm = (s) => (s || '').toString().toLowerCase();

    const filtered = !q
      ? roles
      : roles.filter((rol) => {
          // Match en nombre del rol
          if (norm(rol.nombre).includes(q)) return true;
          // Match en descripción
          if (norm(rol.descripcion).includes(q)) return true;
          // Match en menús con AL MENOS UN permiso activo
          const activeMenuNames = (rol.permisos || [])
            .filter((p) => p.ver || p.crear || p.editar || p.eliminar)
            .map((p) => norm(menusMap[p.menu_id]))
            .filter(Boolean);
          return activeMenuNames.some((name) => name.includes(q));
        });

    return [...filtered].sort((a, b) =>
      a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' })
    );
  }, [roles, menusMap, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  const canCreateRol = canCreate('/roles') || isAdmin();
  const canEditRol = canEdit('/roles') || isAdmin();
  const canDeleteRol = canDelete('/roles') || isAdmin();
  const canViewRol = canView('/roles') || isAdmin();

  if (!canViewRol) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="bg-white border border-[#E8E8E7] shadow-sm p-6">
          <p className="text-[#888888]">No tiene permisos para ver esta página</p>
        </Card>
      </div>
    );
  }

  const hasActiveSearch = !!searchQuery.trim();

  return (
    <div className="skin-page space-y-4 lg:space-y-6" data-testid="roles-page">
      {/* Header */}
      <div className="skin-page-head flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 data-testid="roles-title">Roles</h1>
          <p className="hidden sm:block">Gestiona roles y permisos granulares</p>
        </div>
        {canCreateRol && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="skin-btn skin-btn-primary self-start sm:self-auto"
            data-testid="create-rol-button"
          >
            <Plus size={14} />
            Nuevo Rol
          </button>
        )}
      </div>

      {/* Info Card - Hidden on mobile */}
      <div
        className="skin-panel hidden sm:block"
        style={{ borderLeft: '4px solid var(--skin-accent)' }}
      >
        <div className="p-4 lg:p-6">
          <div className="flex items-start gap-3">
            <Shield size={22} className="mt-1 hidden lg:block" style={{ color: 'var(--skin-accent)' }} />
            <div>
              <h3 className="font-semibold skin-text-ink mb-2 text-sm lg:text-base">
                Sistema de Permisos Granulares
              </h3>
              <p className="text-xs lg:text-sm skin-text-soft mb-2">
                Define permisos específicos para cada rol: <strong>Ver</strong>,{' '}
                <strong>Crear</strong>, <strong>Editar</strong> y{' '}
                <strong>Eliminar</strong> por cada opción del menú.
              </p>
              <p className="text-xs skin-text-mute hidden lg:block">
                Cada usuario tiene asignado un único rol que determina sus permisos en el
                sistema.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 skin-text-mute pointer-events-none"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar por rol, descripción u opción de menú con acceso..."
            className="skin-input pl-9 pr-9"
            data-testid="roles-search-input"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded skin-text-mute hover:skin-text-ink"
              style={{ background: 'transparent' }}
              aria-label="Limpiar búsqueda"
              data-testid="roles-search-clear"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {hasActiveSearch && (
          <span
            className="text-xs skin-text-soft"
            data-testid="roles-search-count"
          >
            {filteredAndSorted.length} de {roles.length} roles
          </span>
        )}
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
        {filteredAndSorted.map((rol) => (
          <Card
            key={rol.id}
            className="skin-panel hover:shadow-md transition-shadow"
            data-testid="rol-card"
          >
            <CardHeader className="p-3 sm:p-4 lg:p-6 pb-2 sm:pb-3">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield
                      size={18}
                      className="sm:w-5 sm:h-5 flex-shrink-0"
                      style={{ color: 'var(--skin-accent)' }}
                    />
                    <CardTitle className="text-base sm:text-lg font-semibold skin-text-ink truncate">
                      {rol.nombre}
                    </CardTitle>
                  </div>
                  <p className="text-xs sm:text-sm skin-text-mute mb-2 sm:mb-3 line-clamp-2">
                    {rol.descripcion || 'Sin descripción'}
                  </p>
                  <span className="skin-badge skin-badge-stage">
                    {getPermissionSummary(rol.permisos)}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 lg:p-6 pt-0">
              <div className="flex gap-2">
                {canEditRol ? (
                  <button
                    type="button"
                    onClick={() => handleEdit(rol)}
                    className="skin-btn flex-1 justify-center"
                    data-testid="edit-rol-button"
                  >
                    <Edit size={14} />
                    Editar
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleView(rol)}
                    className="skin-btn flex-1 justify-center"
                    data-testid="view-rol-button"
                  >
                    <Eye size={14} />
                    Ver
                  </button>
                )}
                {canDeleteRol && rol.nombre !== 'Administrador' && (
                  <button
                    type="button"
                    onClick={() => handleDelete(rol)}
                    className="skin-btn"
                    style={{ color: 'var(--skin-badge-danger-text)', borderColor: 'var(--skin-badge-danger-text)' }}
                    data-testid="delete-rol-button"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Estado vacío contextual */}
      {filteredAndSorted.length === 0 && (
        <Card className="bg-white border border-[#E8E8E7] shadow-sm">
          <CardContent className="text-center py-12">
            <Shield size={48} className="mx-auto mb-4 text-[#888888] opacity-50" />
            {hasActiveSearch ? (
              <>
                <p className="text-[#888888] mb-3">
                  No se encontraron roles que coincidan con{' '}
                  <span className="font-medium text-[#2D2D2D]">
                    "{searchQuery}"
                  </span>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  data-testid="roles-empty-clear-search"
                >
                  Limpiar búsqueda
                </Button>
              </>
            ) : (
              <p className="text-[#888888]">No hay roles configurados</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {showCreate && (
        <CreateRolDialog
          menus={menus}
          open={showCreate}
          onClose={() => {
            setShowCreate(false);
            loadData();
          }}
        />
      )}

      {showEdit && selectedRol && (
        <EditRolDialog
          rol={selectedRol}
          menus={menus}
          open={showEdit}
          readOnly={viewReadOnly}
          onClose={() => {
            setShowEdit(false);
            setSelectedRol(null);
            setViewReadOnly(false);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default Roles;
